import { useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AdminTicketSubcategory } from '@central/contracts';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import {
  alterarSubcategoria,
  apagarSubcategoria,
  chavesDeAdministracao,
  criarSubcategoria,
  listarCategorias,
  listarSubcategorias,
  reordenarSubcategorias,
} from '@/features/admin/admin.api';
import {
  AcoesDaLinha,
  BotoesDeOrdem,
  Cabecalho,
  Campo,
  CascaDeCatalogo,
  Celula,
  Chave,
  chaveSugerida,
  CLASSE_DE_CAMPO,
  ConfirmacaoDeCatalogo,
  ContagemDeUso,
  EtiquetaDeAtividade,
  Linha,
  LinhaDeCabecalho,
  useEscritaDeCatalogo,
  type Intencao,
} from '@/features/admin/componentes';
import { Modal } from '@/features/tickets/Modal';

/**
 * Subcategorias — o segundo e último nível.
 *
 * A tela é filtrada por categoria, e não é uma comodidade: a **ordem da
 * subcategoria vale dentro da categoria**, então reordenar uma lista misturada
 * produziria posições repetidas. Filtrar primeiro é o que torna as setas
 * corretas por construção — o servidor recusa a lista misturada, mas a
 * interface não deveria conseguir montá-la.
 */
export function TicketSubcategoriesPage() {
  const categorias = useQuery({
    queryKey: chavesDeAdministracao.categorias,
    queryFn: listarCategorias,
  });
  const consulta = useQuery({
    queryKey: chavesDeAdministracao.subcategorias,
    queryFn: listarSubcategorias,
  });

  const [categoriaId, setCategoriaId] = useState('');
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<AdminTicketSubcategory | null>(null);
  const [confirmando, setConfirmando] = useState<{
    registro: AdminTicketSubcategory;
    intencao: Intencao;
  } | null>(null);

  const opcoes = categorias.data?.items ?? [];
  // Sem categoria escolhida, a primeira da lista: uma tela que abre vazia
  // parece quebrada, e a primeira categoria é a que o seletor de chamado
  // oferece primeiro.
  const escolhida = categoriaId || opcoes[0]?.id || '';

  const filtradas = useMemo(
    () => (consulta.data?.items ?? []).filter((s) => s.categoryId === escolhida),
    [consulta.data, escolhida],
  );

  const ordenacao = useEscritaDeCatalogo(reordenarSubcategorias);
  const atividade = useEscritaDeCatalogo(
    ({ id, active, version }: { id: string; active: boolean; version: number }) =>
      alterarSubcategoria(id, { active, version }),
    () => setConfirmando(null),
  );
  const remocao = useEscritaDeCatalogo(
    (id: string) => apagarSubcategoria(id),
    () => setConfirmando(null),
  );

  const ocupado =
    ordenacao.mutacao.isPending || atividade.mutacao.isPending || remocao.mutacao.isPending;

  function mover(indice: number, direcao: -1 | 1) {
    const ids = filtradas.map((s) => s.id);
    const destino = indice + direcao;
    [ids[indice], ids[destino]] = [ids[destino] as string, ids[indice] as string];
    ordenacao.mutacao.mutate(ids);
  }

  // A consulta serve só ao estado de carregamento e erro da casca; o corpo
  // da tabela usa `filtradas`, que já é o recorte da categoria escolhida.
  const consultaFiltrada = { ...consulta, data: { items: filtradas } } as typeof consulta;

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <Campo
            id="filtro-categoria"
            rotulo="Categoria"
            dica="A ordem de uma subcategoria vale dentro da categoria dela."
          >
            <select
              id="filtro-categoria"
              value={escolhida}
              onChange={(evento) => setCategoriaId(evento.target.value)}
              className={cn(CLASSE_DE_CAMPO, 'min-w-[16rem]')}
            >
              {opcoes.length === 0 ? <option value="">Nenhuma categoria cadastrada</option> : null}
              {opcoes.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.name}
                  {categoria.active ? '' : ' (inativa)'}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <CascaDeCatalogo
          titulo="Subcategorias"
          resumo="O segundo e último nível. É ele que roteia o chamado e escolhe a política de SLA."
          rotuloDoBotao="Nova subcategoria"
          aoCriar={escolhida ? () => setCriando(true) : undefined}
          consulta={consultaFiltrada}
          vazio={
            opcoes.length === 0
              ? 'Cadastre uma categoria antes: subcategoria solta não existe no vocabulário.'
              : 'Nenhuma subcategoria nesta categoria ainda.'
          }
        >
          <LinhaDeCabecalho>
            <Cabecalho className="w-24">Ordem</Cabecalho>
            <Cabecalho>Subcategoria</Cabecalho>
            <Cabecalho>Chave</Cabecalho>
            <Cabecalho>Em uso</Cabecalho>
            <Cabecalho>Estado</Cabecalho>
            <Cabecalho className="text-right">Ações</Cabecalho>
          </LinhaDeCabecalho>

          <tbody>
            {filtradas.map((subcategoria, indice) => (
              <Linha key={subcategoria.id} inativo={!subcategoria.active}>
                <Celula>
                  <BotoesDeOrdem
                    primeira={indice === 0}
                    ultima={indice === filtradas.length - 1}
                    ocupado={ocupado}
                    aoSubir={() => mover(indice, -1)}
                    aoDescer={() => mover(indice, 1)}
                  />
                </Celula>

                <Celula>
                  <span className="font-medium">{subcategoria.name}</span>
                  {subcategoria.description ? (
                    <span className="mt-0.5 block max-w-md truncate text-xs text-muted-foreground">
                      {subcategoria.description}
                    </span>
                  ) : null}
                </Celula>

                <Celula>
                  <Chave valor={subcategoria.key} />
                </Celula>

                <Celula>
                  <ContagemDeUso uso={subcategoria.usage} />
                </Celula>

                <Celula>
                  <EtiquetaDeAtividade ativo={subcategoria.active} />
                </Celula>

                <Celula>
                  <AcoesDaLinha
                    registro={subcategoria}
                    entidade="subcategoria"
                    ocupado={ocupado}
                    aoEditar={() => setEditando(subcategoria)}
                    aoAlternarAtividade={() => {
                      if (!subcategoria.active) {
                        atividade.mutacao.mutate({
                          id: subcategoria.id,
                          active: true,
                          version: subcategoria.version,
                        });
                        return;
                      }
                      setConfirmando({ registro: subcategoria, intencao: 'desativar' });
                    }}
                    aoRemover={() =>
                      setConfirmando({ registro: subcategoria, intencao: 'remover' })
                    }
                  />
                </Celula>
              </Linha>
            ))}
          </tbody>
        </CascaDeCatalogo>
      </div>

      {ordenacao.erro ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {ordenacao.erro}
        </p>
      ) : null}

      <DialogoDeSubcategoria
        aberto={criando || editando !== null}
        categoriaId={escolhida}
        subcategoria={editando}
        aoFechar={() => {
          setCriando(false);
          setEditando(null);
        }}
      />

      <ConfirmacaoDeCatalogo
        aberto={confirmando !== null}
        intencao={confirmando?.intencao ?? 'desativar'}
        entidade="subcategoria"
        registro={confirmando?.registro ?? null}
        erro={atividade.erro ?? remocao.erro}
        ocupado={ocupado}
        aoFechar={() => setConfirmando(null)}
        aoConfirmar={() => {
          if (!confirmando) return;
          if (confirmando.intencao === 'remover') {
            remocao.mutacao.mutate(confirmando.registro.id);
            return;
          }
          atividade.mutacao.mutate({
            id: confirmando.registro.id,
            active: false,
            version: confirmando.registro.version,
          });
        }}
      />
    </>
  );
}

function DialogoDeSubcategoria({
  aberto,
  categoriaId,
  subcategoria,
  aoFechar,
}: {
  aberto: boolean;
  categoriaId: string;
  subcategoria: AdminTicketSubcategory | null;
  aoFechar: () => void;
}) {
  const editando = subcategoria !== null;

  const [nome, setNome] = useState('');
  const [chave, setChave] = useState('');
  const [chaveTocada, setChaveTocada] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [semeado, setSemeado] = useState<string | null>(null);

  const identidade = subcategoria?.id ?? (aberto ? 'nova' : null);
  if (aberto && semeado !== identidade) {
    setSemeado(identidade);
    setNome(subcategoria?.name ?? '');
    setChave(subcategoria?.key ?? '');
    setChaveTocada(subcategoria !== null);
    setDescricao(subcategoria?.description ?? '');
  }
  if (!aberto && semeado !== null) setSemeado(null);

  const escrita = useEscritaDeCatalogo(async () => {
    if (subcategoria) {
      return alterarSubcategoria(subcategoria.id, {
        name: nome.trim(),
        description: descricao.trim(),
        version: subcategoria.version,
      });
    }
    return criarSubcategoria({
      categoryId: categoriaId,
      key: chave.trim(),
      name: nome.trim(),
      ...(descricao.trim() ? { description: descricao.trim() } : {}),
    });
  }, aoFechar);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={editando ? `Editar “${subcategoria.name}”` : 'Nova subcategoria'}
      descricao={
        editando
          ? 'Nem a chave nem a categoria mudam: os chamados que já apontam para esta subcategoria passariam a significar outra coisa.'
          : 'Ela nasce dentro da categoria selecionada na tela.'
      }
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <Campo id="subcategoria-nome" rotulo="Nome">
          <input
            id="subcategoria-nome"
            value={nome}
            onChange={(evento) => {
              setNome(evento.target.value);
              if (!chaveTocada) setChave(chaveSugerida(evento.target.value));
            }}
            required
            minLength={2}
            maxLength={80}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo
          id="subcategoria-chave"
          rotulo="Chave"
          dica={
            editando
              ? 'Imutável depois de criada.'
              : 'Única dentro da categoria — a mesma chave pode existir sob outra.'
          }
        >
          <input
            id="subcategoria-chave"
            value={chave}
            onChange={(evento) => {
              setChaveTocada(true);
              setChave(evento.target.value);
            }}
            disabled={editando}
            required
            pattern="[a-z0-9][a-z0-9.\-]*[a-z0-9]"
            minLength={2}
            maxLength={60}
            className={`${CLASSE_DE_CAMPO} font-mono text-xs`}
          />
        </Campo>

        <Campo id="subcategoria-descricao" rotulo="Descrição" opcional>
          <textarea
            id="subcategoria-descricao"
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
            rows={2}
            maxLength={500}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            {editando ? 'Salvar' : 'Criar subcategoria'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
